import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Tooltip,
  Typography,
  Snackbar,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  EmailAccount,
  fetchAccounts,
  createAccount,
  deleteAccount,
  getOAuthUrl,
  triggerSync,
} from "./emailHubApi";
import ProviderBadge from "./ProviderBadge";

/* ── Theme (matches EmailInbox) ──────────────────────────────────────── */
const T = {
  navy: "#16233A", gold: "#7C4DFF", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee",
  blue: "#1e40af", blueBg: "#eff6ff",
  red: "#9b2c2c", redBg: "#fef2f2",
};

const EmailHubSettings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ── State ───────────────────────────────────────────────────────── */
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Add account form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newProvider, setNewProvider] = useState<"microsoft" | "google">("microsoft");
  const [newTenantId, setNewTenantId] = useState("");
  const [newClientId, setNewClientId] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<EmailAccount | null>(null);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>({
    open: false, msg: "", severity: "success",
  });

  const showSnack = (msg: string, severity: "success" | "error" = "success") =>
    setSnack({ open: true, msg, severity });

  /* ── Load accounts ───────────────────────────────────────────────── */
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAccounts();
      setAccounts(data);
    } catch { showSnack("Failed to load accounts", "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Show success message if redirected from OAuth callback
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (connected) {
      showSnack(`Successfully connected ${connected}!`);
      loadAccounts();
    }
    if (error) showSnack("OAuth authorization failed. Please try again.", "error");
  }, [searchParams, loadAccounts]);

  /* ── Handlers ────────────────────────────────────────────────────── */
  const handleAddAccount = async () => {
    if (!newEmail.trim()) return;
    try {
      setAddLoading(true);
      await createAccount({
        email: newEmail.trim(),
        display_name: newDisplayName.trim() || undefined,
        provider: newProvider,
        tenant_id: newProvider === "microsoft" ? (newTenantId.trim() || undefined) : undefined,
        client_id: newProvider === "microsoft" ? (newClientId.trim() || undefined) : undefined,
      });
      showSnack(`Account created! Now click 'Connect' to authorize via ${newProvider === "google" ? "Google" : "Microsoft"}.`);
      setNewEmail(""); setNewDisplayName(""); setNewProvider("microsoft"); setNewTenantId(""); setNewClientId("");
      setShowAddForm(false);
      loadAccounts();
    } catch (e: any) {
      showSnack(e?.message || "Failed to create account", "error");
    } finally { setAddLoading(false); }
  };

  const handleConnect = async (account: EmailAccount) => {
    try {
      const { url } = await getOAuthUrl(account.id);
      window.location.href = url;
    } catch { showSnack("Failed to get authorization URL", "error"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAccount(deleteTarget.id);
      showSnack(`Account ${deleteTarget.email} deleted.`);
      setDeleteTarget(null);
      loadAccounts();
    } catch { showSnack("Failed to delete account", "error"); }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await triggerSync();
      showSnack("Sync completed!");
      loadAccounts();
    } catch { showSnack("Sync failed", "error"); }
    finally { setSyncing(false); }
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: T.bg, p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/email-hub")} sx={{ color: T.navy }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: T.navy }}>
              Email Hub Settings
            </Typography>
            <Typography variant="body2" sx={{ color: T.slate }}>
              Manage connected email accounts and OAuth connections
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncOutlinedIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{
              bgcolor: T.white, color: T.navy, border: `1px solid ${T.line}`,
              "&:hover": { bgcolor: T.cream }, textTransform: "none", fontWeight: 600,
            }}
          >
            {syncing ? "Syncing..." : "Sync All"}
          </Button>
          <Button
            startIcon={<AddOutlinedIcon />}
            onClick={() => setShowAddForm(true)}
            sx={{
              bgcolor: T.gold, color: T.white,
              "&:hover": { bgcolor: "#b8963f" }, textTransform: "none", fontWeight: 600,
            }}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Add Account Form */}
      {showAddForm && (
        <Card sx={{ mb: 3, p: 3, border: `1px solid ${T.gold}`, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: T.navy, mb: 2 }}>
            Add New Email Account
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
            <TextField
              select
              label="Provider"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value as "microsoft" | "google")}
              fullWidth
              size="small"
            >
              <MenuItem value="microsoft">Microsoft 365</MenuItem>
              <MenuItem value="google">Google Workspace</MenuItem>
            </TextField>
            <TextField
              label="Email Address"
              placeholder="inquiry@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Display Name"
              placeholder="Step up Academy"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              fullWidth
              size="small"
            />
            {newProvider === "microsoft" && (
              <>
                <TextField
                  label="Azure Tenant ID (optional — uses default from .env)"
                  placeholder="Leave blank to use system default"
                  value={newTenantId}
                  onChange={(e) => setNewTenantId(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Azure Client ID (optional — uses default from .env)"
                  placeholder="Leave blank to use system default"
                  value={newClientId}
                  onChange={(e) => setNewClientId(e.target.value)}
                  fullWidth
                  size="small"
                />
              </>
            )}
            {newProvider === "google" && (
              <TextField
                label="Google Workspace"
                value="Uses GOOGLE_CLIENT_ID / SECRET from .env"
                disabled
                fullWidth
                size="small"
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              onClick={() => { setShowAddForm(false); setNewEmail(""); setNewDisplayName(""); setNewProvider("microsoft"); setNewTenantId(""); setNewClientId(""); }}
              sx={{ color: T.slate, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAccount}
              disabled={addLoading || !newEmail.trim()}
              startIcon={addLoading ? <CircularProgress size={16} /> : <AddOutlinedIcon />}
              sx={{
                bgcolor: T.navy, color: T.white,
                "&:hover": { bgcolor: "#1a2d42" }, textTransform: "none", fontWeight: 600,
              }}
            >
              {addLoading ? "Creating..." : "Create Account"}
            </Button>
          </Box>
        </Card>
      )}

      {/* Accounts Table */}
      <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: T.navy }}>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Email Address</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Display Name</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Provider</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Threads</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Emails</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600 }}>Last Synced</TableCell>
                <TableCell sx={{ color: T.white, fontWeight: 600, textAlign: "center" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={32} sx={{ color: T.gold }} />
                    <Typography variant="body2" sx={{ mt: 1, color: T.slate }}>Loading accounts...</Typography>
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="body1" sx={{ color: T.slate, mb: 1 }}>No email accounts configured</Typography>
                    <Typography variant="body2" sx={{ color: T.slateLt }}>
                      Click "Add Account" to connect your first email inbox.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((acc, idx) => (
                  <TableRow key={acc.id} sx={{ "&:hover": { bgcolor: T.cream }, borderBottom: `1px solid ${T.line}` }}>
                    <TableCell sx={{ color: T.slate }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: T.navy }}>
                        {acc.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: T.slate }}>
                        {acc.display_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ProviderBadge provider={acc.provider} />
                    </TableCell>
                    <TableCell>
                      {acc.is_active ? (
                        <Chip
                          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                          label="Connected"
                          size="small"
                          sx={{ bgcolor: T.greenBg, color: T.green, fontWeight: 600, fontSize: 12 }}
                        />
                      ) : (
                        <Chip
                          icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                          label="Not Connected"
                          size="small"
                          sx={{ bgcolor: T.redBg, color: T.red, fontWeight: 600, fontSize: 12 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: T.navy, fontWeight: 500 }}>
                        {acc.threads_count ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: T.navy, fontWeight: 500 }}>
                        {acc.emails_count ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: T.slate, fontSize: 12 }}>
                        {acc.last_synced_at
                          ? new Date(acc.last_synced_at).toLocaleString()
                          : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <Tooltip title={acc.is_active ? "Re-authorize" : "Connect via Microsoft"}>
                          <IconButton size="small" onClick={() => handleConnect(acc)} sx={{ color: T.blue }}>
                            <LinkOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete account">
                          <IconButton size="small" onClick={() => setDeleteTarget(acc)} sx={{ color: T.red }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Info Card */}
      <Card sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: T.blueBg, border: `1px solid ${T.blue}22` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: T.blue, mb: 1 }}>
          How it works
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: T.slate, "& li": { mb: 0.5, fontSize: 14 } }}>
          <li><strong>Add Account</strong> — Enter the email address (e.g. inquiry@example.com). Leave Tenant/Client ID blank to use system defaults.</li>
          <li><strong>Connect</strong> — Click the link icon to authorize via Microsoft OAuth. You will be redirected to Microsoft login.</li>
          <li><strong>Sync</strong> — Once connected, click "Sync All" to fetch emails. The system also syncs automatically.</li>
          <li><strong>Multiple Accounts</strong> — Add as many accounts as needed. Each gets its own OAuth connection and inbox.</li>
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontWeight: 700, color: T.navy }}>Delete Email Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{deleteTarget?.email}</strong> and all its synced emails,
            threads, and attachments. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: T.slate, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{ bgcolor: T.red, color: T.white, "&:hover": { bgcolor: "#7f1d1d" }, textTransform: "none", fontWeight: 600 }}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailHubSettings;
